import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import DataSync from "lib/api/DataSync";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";
import PageLayout from "components/layouts/PageLayout";
import { exportRecordsAsCSV } from "lib/utils/csv";


const CLOUD_TYPE_LABELS = {
  clddeltek: "AWS",
  cldzd: "Zimcom-Dedicated",
  cldzs: "Zimcom",
};

function parseCloudType(celicense) {
  if (!celicense) return null;
  const lines = celicense.split(/\r?\n/);
  for (const key of Object.keys(CLOUD_TYPE_LABELS)) {
    if (lines.some((l) => l.trim() === `module=${key}`)) return key;
  }
  return null;
}

const SEVEN_DAYS_MS  = 7 * 24 * 60 * 60 * 1000;
const ONE_DAY_MS     = 24 * 60 * 60 * 1000;
const THREE_DAYS_MS  = 72 * 60 * 60 * 1000;

function isFriToSun(date) {
  const day = new Date(date).getDay(); // 0=Sun, 5=Fri, 6=Sat
  return day === 0 || day === 5 || day === 6;
}

function isStalled(record) {
  if (!record?.client_uuid) return false;
  if (record.provisioning_status === "deleted") return false;

  const now = Date.now();

  // Stage 1: Invitation Sent → Init. Complete (7 days from created_at)
  const initComplete = isInitializationComplete(record);
  if (!initComplete && record.created_at) {
    if (now - new Date(record.created_at).getTime() > SEVEN_DAYS_MS) return true;
  }

  // Stage 2: Init. Complete → Finalize Started (7 days, using last_activity_at as proxy)
  if (initComplete && !record.finalized_started_at && record.last_activity_at) {
    if (now - new Date(record.last_activity_at).getTime() > SEVEN_DAYS_MS) return true;
  }

  // Stage 3: Finalize Started → Finalize Complete (7 days from finalized_started_at)
  if (record.finalized_started_at && !record.finalized_at) {
    if (now - new Date(record.finalized_started_at).getTime() > SEVEN_DAYS_MS) return true;
  }

  // Stage 4: Finalize Complete → Customer Live (24h M–Th, 72h F–Su based on finalized_at day)
  // Only applies while they're still waiting to go live — once customer_live_email_sent_at
  // is set, they're done with this stage and should never be re-flagged Stalled.
  const finalizeFailed = isFinalizeFailed(record);
  const finalizeComplete =
    !finalizeFailed &&
    record.finalization_status &&
    record.finalization_status !== "none" &&
    record.finalized_at &&
    new Date(record.finalized_at) < new Date();
  if (finalizeComplete && !record.customer_live_email_sent_at) {
    const threshold = isFriToSun(record.finalized_at) ? THREE_DAYS_MS : ONE_DAY_MS;
    if (now - new Date(record.finalized_at).getTime() > threshold) return true;
  }

  return false;
}

function getRecordContacts(record) {
  return record?.client?.settings?.contacts || [];
}

function isInitializationStarted(record) {
  if (!record?.client_uuid) return false;
  const dataToSync = parseFloat(record.total_data_to_sync_mb || 0);
  const cloudUserCount = Number(record.client?.cloud_user_count || 0);
  const contactCount = getRecordContacts(record).length;
  return dataToSync > 0 && cloudUserCount > 0 && contactCount > 0;
}

function isFinalizeFailed(record) {
  if (!record?.client_uuid) return false;
  const hasFailedFiles = record.failed_files != null && Number(record.failed_files) > 0;
  const startedButNeverFinished =
    record.finalized_started_at &&
    !record.finalized_at &&
    record.finalization_status !== "waiting";
  return hasFailedFiles || startedButNeverFinished;
}

function isFinalizeComplete(record) {
  if (!record?.client_uuid) return false;
  const finalized = record.finalization_status && record.finalization_status !== "none";
  const finalizedInPast = record.finalized_at && new Date(record.finalized_at) < new Date();
  return finalized && finalizedInPast;
}

// "Syncing" means actively transferring data and not yet finalized — either the backend
// currently reports the syncing activity, or files have already started coming across.
function isSyncing(record) {
  if (!record?.client_uuid) return false;
  if (isFinalizeComplete(record)) return false;
  const activityIsSyncing = record.current_activity && toProperCase(record.current_activity) === "Syncing";
  const hasFilesSynced = Number(record.files_synced || 0) > 0;
  return activityIsSyncing || hasFilesSynced;
}

function isInitializationComplete(record) {
  if (!record?.client_uuid) return false;
  const dataToSync = parseFloat(record.total_data_to_sync_mb || 0);
  const dataSynced = parseFloat(record.total_data_synced_mb || 0);
  const cloudUserCount = Number(record.client?.cloud_user_count || 0);
  const contactCount = getRecordContacts(record).length;
  return (
    contactCount > 0 &&
    cloudUserCount > 0 &&
    dataToSync > 0 &&
    dataSynced > 0 &&
    dataSynced >= dataToSync
  );
}

function computeActiveStatuses(record) {
  const initComplete = isInitializationComplete(record);
  const finalizeFailed = isFinalizeFailed(record);
  const activeStatuses = new Set();
  if (record.current_activity) {
    const activityLabel = toProperCase(record.current_activity);
    if (activityLabel !== "Syncing") activeStatuses.add(activityLabel);
  }
  if (isSyncing(record)) activeStatuses.add("Syncing");
  if (!initComplete && isInitializationStarted(record)) activeStatuses.add("Init. Started");
  if (initComplete) activeStatuses.add("Init. Complete");
  if (!finalizeFailed && isFinalizeComplete(record)) activeStatuses.add("Finalize Complete");
  if (finalizeFailed) activeStatuses.add("Finalize Failed");
  if (record.customer_live_email_sent_at) activeStatuses.add("Customer Live (email sent)");
  if (getRecordContacts(record).length === 0) activeStatuses.add("Missing Contacts");
  if (isStalled(record)) activeStatuses.add("Stalled");
  return activeStatuses;
}

// Flattened export row — intentionally omits client.permissions (celicense embeds API
// keys/signatures) and client.settings.contacts, keeping only what's shown on screen
// plus the lifecycle timestamps.
function buildExportRow(record) {
  const cloudTypeKey = parseCloudType(record.client?.permissions?.celicense);
  return {
    "Client UUID": record.client_uuid,
    "Customer Name": record.client?.name || "",
    "Deltek ID": record.client?.permissions?.deltekid || "",
    "License #": record.client?.code || "",
    "Cloud Environment": cloudTypeKey ? (CLOUD_TYPE_LABELS[cloudTypeKey] || cloudTypeKey) : "",
    "Cloud User Count": record.client?.cloud_user_count || "",
    "Migration Status": Array.from(computeActiveStatuses(record)).join("; "),
    "Provisioning Status": record.provisioning_status || "",
    "Sync Status": record.sync_status || "",
    "Files Synced": record.files_synced ?? "",
    "Total Data To Sync (MB)": record.total_data_to_sync_mb ?? "",
    "Total Data Synced (MB)": record.total_data_synced_mb ?? "",
    "Finalization Status": record.finalization_status || "",
    "Finalized File Count": record.finalized_file_count ?? "",
    "Pending Files": record.pending_files ?? "",
    "Failed Files": record.failed_files ?? "",
    "Invitation Email Sent At": record.invitation_email_sent_at || "",
    "Customer Live Email Sent At": record.customer_live_email_sent_at || "",
    "Revert Email Sent At": record.revert_email_sent_at || "",
    "Waiting Email Sent At": record.waiting_email_sent_at || "",
    "Finalized Started At": record.finalized_started_at || "",
    "Finalized At": record.finalized_at || "",
    "Last Activity At": record.last_activity_at || "",
    "Cleanup Completed At": record.cleanup_completed_at || "",
    "Created At": record.created_at || "",
    "Updated At": record.updated_at || "",
  };
}

const ACTIVITY_COLORS = {
  "Reverted": { bg: "#f3f4f6", color: "#4b5563" },
  "Invitation Sent": { bg: "#dbeafe", color: "#1d4ed8" },
  "Invitation Accepted": { bg: "#dbeafe", color: "#1d4ed8" },
  "Initialization Started": { bg: "#fef3c7", color: "#b45309" },
  "Cloud Provisioned": { bg: "#e0e7ff", color: "#4338ca" },
  "Syncing": { bg: "#d1fae5", color: "#065f46" },
  "Completed": { bg: "#d1fae5", color: "#065f46" },
};

const DERIVED_BADGE_COLORS = {
  initStarted:      { bg: "#fff7ed", color: "#c2410c" },
  initComplete:     { bg: "#f0fdf4", color: "#15803d" },
  finalizeComplete: { bg: "#eff6ff", color: "#1d4ed8" },
  finalizeFailed:   { bg: "#fef2f2", color: "#991b1b" },
  missingContacts:  { bg: "#fdf4ff", color: "#7e22ce" },
  stalled:          { bg: "#fee2e2", color: "#dc2626" },
  syncing:          { bg: "#d1fae5", color: "#065f46" },
};

function toProperCase(str) {
  if (!str) return str;
  return str
    .replace(/[_-]/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

const PILL_STYLE = {
  padding: "3px 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

function ActivityBadge({ activity, syncing, stalled, initStarted, initComplete, finalizeComplete, finalizeFailed, missingContacts }) {
  const hasDerived = syncing || stalled || initStarted || initComplete || finalizeComplete || finalizeFailed || missingContacts;
  const label = toProperCase(activity);
  const showRawActivity = activity && label !== "Syncing";
  if (!showRawActivity && !hasDerived) return <span style={{ color: "#9ca3af" }}>-</span>;
  const colors = ACTIVITY_COLORS[label] || ACTIVITY_COLORS[activity] || { bg: "#f3f4f6", color: "#4b5563" };
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {showRawActivity && (
        <span style={{ ...PILL_STYLE, backgroundColor: colors.bg, color: colors.color }}>
          {label}
        </span>
      )}
      {syncing && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.syncing.bg, color: DERIVED_BADGE_COLORS.syncing.color }}>
          Syncing
        </span>
      )}
      {initStarted && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.initStarted.bg, color: DERIVED_BADGE_COLORS.initStarted.color }}>
          Init. Started
        </span>
      )}
      {initComplete && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.initComplete.bg, color: DERIVED_BADGE_COLORS.initComplete.color }}>
          Init. Complete
        </span>
      )}
      {finalizeComplete && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.finalizeComplete.bg, color: DERIVED_BADGE_COLORS.finalizeComplete.color }}>
          Finalize Complete
        </span>
      )}
      {finalizeFailed && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.finalizeFailed.bg, color: DERIVED_BADGE_COLORS.finalizeFailed.color }}>
          Finalize Failed
        </span>
      )}
      {missingContacts && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.missingContacts.bg, color: DERIVED_BADGE_COLORS.missingContacts.color }}>
          Missing Contacts
        </span>
      )}
      {stalled && (
        <span style={{ ...PILL_STYLE, backgroundColor: DERIVED_BADGE_COLORS.stalled.bg, color: DERIVED_BADGE_COLORS.stalled.color }}>
          Stalled
        </span>
      )}
    </span>
  );
}

function formatStatusDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  return `${month} ${day}, ${year}, ${hours}:${minutes} UTC`;
}

const tableStyles = `
  .cloud-migrations-table .ReactVirtualized__Table__headerRow {
    background-color: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #6b7280;
  }
  .cloud-migrations-table .ReactVirtualized__Table__headerColumn {
    display: flex;
    align-items: center;
    padding: 0 12px;
    overflow: visible !important;
  }
  .cloud-migrations-table .ReactVirtualized__Table__row {
    border-bottom: 1px solid #f3f4f6;
    background: #ffffff;
    transition: background 0.1s;
  }
  .cloud-migrations-table .ReactVirtualized__Table__row:hover {
    background-color: #f9fafb;
  }
  .cloud-migrations-table .ReactVirtualized__Table__rowColumn {
    display: flex;
    align-items: center;
    padding: 0 12px;
    overflow: hidden;
    font-size: 14px;
    color: #111827;
  }
  .cloud-migrations-table .ReactVirtualized__Grid {
    outline: none;
  }
  .cloud-migrations-table .ReactVirtualized__Table__sortableHeaderColumn {
    cursor: default;
  }
  .migration-view-btn {
    font-size: 12px;
    padding: 4px 14px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    font-weight: 500;
    text-decoration: none;
    white-space: nowrap;
    display: inline-block;
    transition: background 0.1s, border-color 0.1s;
  }
  .migration-view-btn:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #111827;
    text-decoration: none;
  }
  .qv-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .qv-chip:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }
  .qv-chip--active {
    border-color: #fca5a5;
    background: #fff1f2;
    color: #b91c1c;
  }
  .qv-chip--active:hover {
    background: #fee2e2;
    border-color: #f87171;
  }
  .qv-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    background: #ef4444;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }
`;

const MIGRATION_STATUS_OPTIONS = [
  "All",
  "Reverted",
  "Invitation Sent",
  "Invitation Accepted",
  "Initialization Started",
  "Cloud Provisioned",
  "Syncing",
  // "Completed", // confusing alongside "Finalize Complete" — matches raw current_activity, not the finalization pipeline
  "Init. Started",
  "Init. Complete",
  "Finalize Complete",
  "Finalize Failed",
  "Customer Live (email sent)",
  "Missing Contacts",
  "Stalled",
];

const CLOUD_ENV_OPTIONS = ["All", ...Object.values(CLOUD_TYPE_LABELS)];

const filterBarStyles = `
  .filter-bar {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 14px 16px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .filter-bar__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 100px;
  }
  .filter-bar__field--narrow {
    flex: 0 0 110px;
  }
  .filter-bar__label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    white-space: nowrap;
  }
  .filter-bar__input {
    height: 34px;
    padding: 0 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    color: #111827;
    background: #ffffff;
    outline: none;
    width: 100%;
    box-sizing: border-box;
  }
  .filter-bar__input:focus {
    border-color: #6b7280;
  }
  .filter-bar__clear {
    height: 34px;
    padding: 0 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #ffffff;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    align-self: flex-end;
  }
  .filter-bar__clear:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
`;

export default function DataSyncList() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quickViewFilter, setQuickViewFilter] = useState(null);

  const [filterCustomerName, setFilterCustomerName] = useState("");
  const [filterLicense, setFilterLicense] = useState("");
  const [filterDeltekId, setFilterDeltekId] = useState("");
  const [filterDesktopVersion, setFilterDesktopVersion] = useState("");
  const [filterMigrationStatus, setFilterMigrationStatus] = useState("All");
  const [filterCloudEnvironment, setFilterCloudEnvironment] = useState("All");
  const [filterCloudUsersMin, setFilterCloudUsersMin] = useState("");
  const [statusDateSort, setStatusDateSort] = useState("desc"); // null | 'asc' | 'desc'
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const match = document.cookie.match(/(?:^|;\s*)migrations_auto_refresh=([^;]*)/);
    return match ? match[1] === "true" : false;
  });
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const autoRefreshRef = useRef(null);

  const [loadingAll, setLoadingAll] = useState(false);

  const recordsPerPage = 100;
  const loadingAllRef = useRef(false);
  const hasActiveFiltersRef = useRef(false);

  async function load() {
    // With filters active, refetch everything before swapping so matches beyond page 1 don't vanish.
    const fetchAll = hasActiveFiltersRef.current;
    if (fetchAll) {
      loadingAllRef.current = true;
      setLoadingAll(true);
    }
    setLoading(true);
    try {
      let response = await DataSync.get({ offset: 0, limit: fetchAll ? 500 : recordsPerPage });
      let all = response.data;
      let currentTotal = response.total;
      while (fetchAll && all.length < currentTotal && response.data.length) {
        response = await DataSync.get({ offset: all.length, limit: 500 });
        all = [...all, ...response.data];
        currentTotal = response.total;
      }
      setRecords(all);
      setTotal(currentTotal);
      setLastRefreshed(new Date());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    if (fetchAll) {
      loadingAllRef.current = false;
      setLoadingAll(false);
    }
  }

  function isRowLoaded({ index }) {
    return !!records[index];
  }

  async function loadMoreRows({ startIndex, stopIndex }) {
    if (loading || loadingAllRef.current || records.length >= total) return;
    setLoading(true);
    try {
      const params = {
        offset: records.length,
        limit: Math.max(recordsPerPage, stopIndex - records.length + 1),
      };
      const response = await DataSync.get(params);
      if (response.total !== total) setTotal(response.total);
      setRecords((prev) => [...prev, ...response.data]);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // Fetches whatever pages of the dataset aren't already in `records`, without touching
  // component state — used by both loadAllRows() (status filters) and exportToCSV().
  async function fetchAllRecords(pageSize = recordsPerPage) {
    let all = records;
    let currentTotal = total;
    let offset = all.length;
    while (offset < currentTotal) {
      const response = await DataSync.get({ offset, limit: pageSize });
      if (!response.data.length) break;
      all = [...all, ...response.data];
      currentTotal = response.total;
      offset += response.data.length;
    }
    return { all, currentTotal };
  }

  // Status filters (Migration Status, Stalled quick view) are computed client-side
  // from whatever is in `records`, so they need the full dataset loaded rather than
  // just the page the infinite-scroll table has fetched so far.
  async function loadAllRows() {
    if (loadingAllRef.current) return;
    loadingAllRef.current = true;
    setLoadingAll(true);
    setLoading(true);
    try {
      const { all, currentTotal } = await fetchAllRecords(500);
      setRecords(all);
      setTotal(currentTotal);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    loadingAllRef.current = false;
    setLoadingAll(false);
  }

  async function exportToCSV() {
    setLoading(true);
    try {
      const { all, currentTotal } = await fetchAllRecords(500);
      setRecords(all);
      setTotal(currentTotal);

      const withClient = all.filter((r) => r.client_uuid);
      const matches = quickViewFilter === "stalled" ? withClient.filter(isStalled) : withClient.filter(applyFilters);
      const rows = matches.map(buildExportRow);

      await exportRecordsAsCSV(rows, [], "cloud-migrations-export.csv");
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    return () => setLoading(false);
  }, []);

  // All filters run client-side over `records`, so any active filter needs the full dataset.
  const hasActiveFilters =
    quickViewFilter === "stalled" ||
    filterCustomerName !== "" ||
    filterLicense !== "" ||
    filterDeltekId !== "" ||
    filterDesktopVersion !== "" ||
    filterMigrationStatus !== "All" ||
    filterCloudEnvironment !== "All" ||
    filterCloudUsersMin !== "";
  hasActiveFiltersRef.current = hasActiveFilters;

  useEffect(() => {
    if (hasActiveFilters && records.length < total) {
      loadAllRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters, total, records.length]);

  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(load, 2 * 60 * 1000);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh]);

  function applyFilters(record) {
    if (!record?.client_uuid) return true;
    if (
      filterCustomerName &&
      !String(record.client?.name || "").toLowerCase().includes(filterCustomerName.toLowerCase())
    )
      return false;
    if (
      filterLicense &&
      !String(record.client?.code || "").toLowerCase().includes(filterLicense.toLowerCase())
    )
      return false;
    if (
      filterDeltekId &&
      !String(record.client?.permissions?.deltekid || "").toLowerCase().includes(filterDeltekId.toLowerCase())
    )
      return false;
    if (
      filterDesktopVersion &&
      !String(record.client?.desktop_version || "").toLowerCase().includes(filterDesktopVersion.toLowerCase())
    )
      return false;
    if (filterMigrationStatus !== "All") {
      if (!computeActiveStatuses(record).has(filterMigrationStatus)) return false;
    }
    if (filterCloudEnvironment !== "All") {
      const key = parseCloudType(record.client?.permissions?.celicense);
      if (CLOUD_TYPE_LABELS[key] !== filterCloudEnvironment) return false;
    }
    if (filterCloudUsersMin !== "") {
      const count = Number(record.client?.cloud_user_count || 0);
      if (count < Number(filterCloudUsersMin)) return false;
    }
    return true;
  }

  function clearFilters() {
    setFilterCustomerName("");
    setFilterLicense("");
    setFilterDeltekId("");
    setFilterDesktopVersion("");
    setFilterMigrationStatus("All");
    setFilterCloudEnvironment("All");
    setFilterCloudUsersMin("");
  }

  function cycleStatusDateSort() {
    setStatusDateSort((prev) => (prev === null ? "desc" : prev === "desc" ? "asc" : null));
  }

  const stalledRecords = records.filter(isStalled);
  const stalledCount = stalledRecords.length;

  const baseRecords = quickViewFilter === "stalled" ? stalledRecords : records;
  const baseTotal = quickViewFilter === "stalled" ? stalledRecords.length : total;
  const filteredRecords = baseRecords.filter(applyFilters);
  const activeRecords = statusDateSort
    ? [...filteredRecords].sort((a, b) => {
        const aDate = a.last_activity_at || "";
        const bDate = b.last_activity_at || "";
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return statusDateSort === "asc"
          ? aDate.localeCompare(bDate)
          : bDate.localeCompare(aDate);
      })
    : filteredRecords;
  const activeTotal = activeRecords.length;
  // Unfiltered: report the server total so scrolling to the end triggers the next page.
  const tableRowCount = hasActiveFilters ? activeTotal : Math.max(total, activeTotal);

  function toggleStalled() {
    setQuickViewFilter((prev) => (prev === "stalled" ? null : "stalled"));
  }

  return (
    <PageLayout>
      <style>{tableStyles}</style>
      <style>{filterBarStyles}</style>
      <h1>Cloud Migrations</h1>
      <div className="hr hr--darkest"></div>

      <div className="filter-bar">
        <div className="filter-bar__field">
          <span className="filter-bar__label">Customer Name</span>
          <input
            className="filter-bar__input"
            placeholder="Filter..."
            value={filterCustomerName}
            onChange={(e) => setFilterCustomerName(e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">License # / Tenant</span>
          <input
            className="filter-bar__input"
            placeholder="Filter..."
            value={filterLicense}
            onChange={(e) => setFilterLicense(e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Deltek ID</span>
          <input
            className="filter-bar__input"
            placeholder="Filter..."
            value={filterDeltekId}
            onChange={(e) => setFilterDeltekId(e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Desktop Version</span>
          <input
            className="filter-bar__input"
            placeholder="Filter..."
            value={filterDesktopVersion}
            onChange={(e) => setFilterDesktopVersion(e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Migration Status</span>
          <select
            className="filter-bar__input"
            value={filterMigrationStatus}
            onChange={(e) => setFilterMigrationStatus(e.target.value)}
          >
            {MIGRATION_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Cloud Environment</span>
          <select
            className="filter-bar__input"
            value={filterCloudEnvironment}
            onChange={(e) => setFilterCloudEnvironment(e.target.value)}
          >
            {CLOUD_ENV_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div className="filter-bar__field filter-bar__field--narrow">
          <span className="filter-bar__label"># Cloud Users (min)</span>
          <input
            className="filter-bar__input"
            type="number"
            min="0"
            placeholder=""
            value={filterCloudUsersMin}
            onChange={(e) => setFilterCloudUsersMin(e.target.value)}
          />
        </div>
        <button className="filter-bar__clear" onClick={clearFilters}>
          Clear
        </button>
        {(loading || loadingAll) && (
          <div className="loading-container" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
            <div className="loading-icon"></div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, marginTop: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#9ca3af",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Quick View:
        </span>
        <button
          className={`qv-chip${quickViewFilter === "stalled" ? " qv-chip--active" : ""}`}
          onClick={toggleStalled}
        >
          Stalled · &gt;7 days since invitation or initialization
          <span className="qv-badge">{stalledCount}</span>
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>
          {quickViewFilter === "stalled"
            ? `${activeTotal} stalled record${activeTotal !== 1 ? "s" : ""} (${records.length} of ${total} loaded)`
            : `${activeTotal} record${activeTotal !== 1 ? "s" : ""} (${records.length} of ${total} loaded)`}
          {hasActiveFilters && (loadingAll || records.length < total) && (
            <span style={{ marginLeft: 10, color: "#b45309", fontWeight: 500 }}>
              Loading all records… results may be incomplete
            </span>
          )}
          {lastRefreshed && (
            <span style={{ marginLeft: 10, color: "#9ca3af" }}>
              Last refreshed {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={load}
            disabled={loading}
            style={{ height: 30, padding: "0 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#ffffff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            onClick={() => setAutoRefresh((v) => {
              const next = !v;
              document.cookie = `migrations_auto_refresh=${next};path=/;max-age=${60 * 60 * 24 * 365}`;
              return next;
            })}
            style={{ height: 30, padding: "0 12px", border: `1px solid ${autoRefresh ? "#6ee7b7" : "#d1d5db"}`, borderRadius: 6, background: autoRefresh ? "#ecfdf5" : "#ffffff", color: autoRefresh ? "#065f46" : "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
          >
            {autoRefresh ? "Auto Refresh: ON" : "Auto Refresh: OFF"}
          </button>
          <button
            onClick={exportToCSV}
            disabled={loading}
            style={{ height: 30, padding: "0 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#ffffff", color: "#374151", fontSize: 13, fontWeight: 500, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Exporting..." : "Export to CSV"}
          </button>
        </div>
      </div>

      <div
        className="cloud-migrations-table"
        style={{
          background: "#ffffff",
          borderRadius: 8,
          outline: "1px solid #e5e7eb",
          overflowX: "auto",
          overflowY: "hidden",
          flex: 1,
          minHeight: 0,
          height: "calc(100vh - 320px)",
        }}
      >
        <InfiniteLoader
          isRowLoaded={({ index }) => hasActiveFilters || index < records.length}
          loadMoreRows={hasActiveFilters ? () => Promise.resolve() : loadMoreRows}
          rowCount={tableRowCount}
          threshold={15}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ height, width }) => {
                const tableWidth = Math.max(width, 1060);
                return (
                <Table
                  headerHeight={44}
                  width={tableWidth}
                  height={height}
                  rowCount={tableRowCount}
                  rowHeight={80}
                  rowGetter={({ index }) => activeRecords[index] || {}}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                  gridStyle={{ outline: "none" }}
                >
                  <Column
                    label="Customer Name"
                    dataKey="client"
                    width={tableWidth * 0.18}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>Loading...</span>;
                      return (
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 500, color: "#111827" }}>
                            {rowData.client?.name || rowData.client_uuid}
                          </span>
                          <Link
                            to={`/client/${rowData.client_uuid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="migration-view-btn"
                            style={{ fontSize: 13, padding: "1px 6px", flexShrink: 0 }}
                            title="View Client"
                          >
                            ↗
                          </Link>
                        </div>
                      );
                    }}
                  />
                  <Column
                    label="Deltek ID"
                    dataKey="client"
                    width={tableWidth * 0.07}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      return <span style={{ color: "#4b5563" }}>{rowData.client?.permissions?.deltekid || "-"}</span>;
                    }}
                  />
                  <Column
                    label="License #"
                    dataKey="client"
                    width={tableWidth * 0.07}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      return <span style={{ color: "#4b5563" }}>{rowData.client?.code || "-"}</span>;
                    }}
                  />
                  <Column
                    label="Desktop Version"
                    dataKey="client"
                    width={tableWidth * 0.09}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      return <span style={{ color: "#4b5563" }}>{rowData.client?.desktop_version || "-"}</span>;
                    }}
                  />
                  <Column
                    label="Migration Status"
                    dataKey="current_activity"
                    width={tableWidth * 0.20}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      const initComplete = isInitializationComplete(rowData);
                      const finalizeFailed = isFinalizeFailed(rowData);
                      return (
                        <ActivityBadge
                          activity={cellData}
                          syncing={isSyncing(rowData)}
                          stalled={isStalled(rowData)}
                          initStarted={!initComplete && isInitializationStarted(rowData)}
                          initComplete={initComplete}
                          finalizeComplete={!finalizeFailed && isFinalizeComplete(rowData)}
                          finalizeFailed={finalizeFailed}
                          missingContacts={getRecordContacts(rowData).length === 0}
                        />
                      );
                    }}
                  />
                  <Column
                    label="Status Date"
                    dataKey="last_activity_at"
                    width={tableWidth * 0.15}
                    headerRenderer={() => (
                      <div
                        onClick={cycleStatusDateSort}
                        style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none", fontSize: 11, fontWeight: 700, color: statusDateSort ? "#111827" : "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase" }}
                        title="Sort by Status Date"
                      >
                        Status Date
                        {statusDateSort && (
                          <span style={{ fontSize: 12 }}>
                            {statusDateSort === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    )}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      return (
                        <span style={{ color: "#6b7280", fontSize: 13 }}>
                          {formatStatusDate(cellData)}
                        </span>
                      );
                    }}
                  />
                  <Column
                    label="# Cloud Users"
                    dataKey="client"
                    width={tableWidth * 0.06}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      const count = rowData.client?.cloud_user_count;
                      return (
                        <span style={{ color: "#4b5563" }}>
                          {count != null ? Number(count).toLocaleString() : "-"}
                        </span>
                      );
                    }}
                  />
                  <Column
                    label="Cloud Environment"
                    dataKey="client"
                    width={tableWidth * 0.10}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.client_uuid) return <span style={{ color: "#9ca3af" }}>—</span>;
                      const key = parseCloudType(rowData.client?.permissions?.celicense);
                      return <span style={{ color: "#4b5563" }}>{key ? CLOUD_TYPE_LABELS[key] : "-"}</span>;
                    }}
                  />
                  <Column
                    label="Actions"
                    dataKey="client_uuid"
                    width={tableWidth * 0.08}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) return null;
                      return (
                        <Link className="migration-view-btn" to={`/data-sync/${cellData}`}>
                          View Migration
                        </Link>
                      );
                    }}
                  />
                </Table>
                );
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
    </PageLayout>
  );
}
